import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { getApps, getAssetsFile, getDefaultAssetsFile } from '@/api';
import { ThemeProvider } from 'react-jss';
import { theme } from '@/theme';
import { Alert, Loader } from '@/components';
import { useStyles } from './styles';
import { getAttendantUser } from '@/utils';
import { VideoProvider } from './VideoProvider';
import { BulkRegistrationProvider } from '@/GlobalProvider/BulkRegistrationProvider';
import { GoogleTagManagerSetter } from '@/gtag/GoogleTagManagerSetter';

import { useAppData } from '@/zustand/useAppData';
import { useAssetsData } from '@/zustand/useAssetsData';
import { useAttendantData } from '@/zustand/useAttendantData';
import { useFrameData } from '@/zustand/useFrameData';
import { useAlertData } from '@/zustand/useAlertData';

const defaultAssetsUrl = process.env.REACT_APP__DEFAULT_ASSETS_URL;
const assetsUrl = process.env.REACT_APP_ASSETS_URL;
const assetsFileVersion = process.env.REACT_APP_ASSETS_FILE_VERSION;

export const GlobalProvider = ({ children }) => {
  const styles = useStyles();
  const { appData, setAppData } = useAppData((store) => store);
  const setAssetsData = useAssetsData((store) => store.setAssetsData);
  const { token, setAttendantData, removeAttendantData } = useAttendantData(
    (store) => store
  );
  const setIsInIframe = useFrameData((store) => store.setIsInIframe);
  setIsInIframe();
  const { alert, setAlertData } = useAlertData((store) => store);

  const [loading, setLoading] = useState(false);

  const attendantUserSetUp = useCallback(() => {
    if (token) {
      const attendantUser = getAttendantUser(token);
      const currentTimeInSeconds = Date.now() / 1000;
      if (currentTimeInSeconds < attendantUser.exp) {
        setAttendantData(attendantUser);
      } else {
        removeAttendantData();
        setAlertData({
          variation: 'error',
          text: React.createElement(
            'span',
            null,
            'Session has expire: Please login again'
          )
        });
      }
    }
  }, []);

  useEffect(() => {
    const getAppData = async () => {
      setLoading(true);
      attendantUserSetUp();
      const response = await getApps();
      const res = await getAssetsFile(response[0].id);
      if (res && res.asset_file_version === assetsFileVersion) {
        setAssetsData({ ...res, imageUrl: `${assetsUrl}/${response[0].id}` });
      } else {
        const defaultRes = await getDefaultAssetsFile();
        if (defaultRes?.asset_file_version === assetsFileVersion) {
          setAssetsData({ ...defaultRes, imageUrl: defaultAssetsUrl });
        }
      }
      if (response) {
        setAppData(response[0]);
      }

      setLoading(false);
    };
    getAppData();
  }, []);

  if (loading)
    return (
      <ThemeProvider theme={theme}>
        <div className={styles.wrapper}>
          <Loader />
        </div>
      </ThemeProvider>
    );

  if (!appData) return null;

  return (
    <>
      <ThemeProvider theme={theme}>
        <BulkRegistrationProvider>
          <VideoProvider>{children}</VideoProvider>
        </BulkRegistrationProvider>
      </ThemeProvider>
      {alert && (
        <Alert {...alert} onDismiss={() => setAlertData(null)}>
          <>{alert.text}</>
        </Alert>
      )}
      <GoogleTagManagerSetter consentGiven={true} />
    </>
  );
};
